import { Controller } from "@hotwired/stimulus";
import { safeParse, formatCurrency, computeCartETag, persistString, persistToLocalStorage } from "../utils/cart_utils";

// Connects to data-controller="cart"
export default class extends Controller {
  static targets = ["cartItemTemplate", "list", "spinner", "subtotal", "checkoutBtn", "spykeCartOffcanvas", "stripeCartOffcanvas"];
  static values = {};

  connect() {}

  // Triggered when the Bootstrap offcanvas is shown
  onOffcanvasShow() {
    console.log("Cart offcanvas shown, checking for updates...");

    try {
      // Show loading state while we render
      this._setLoading(true);

      // Read server-provided etag (if present) and local etag
      const currentEtag = String(this.element.dataset.currentCartEtag || "");
      const localEtag = String(localStorage.getItem("cart_etag") || "");

      // If both sides have a non-empty etag and they match, we are up to date
      if (currentEtag && localEtag && currentEtag === localEtag) {
        this._setLoading(false);
        return;
      }

      // console.log("Cart out of date, re-rendering...");

      // Load cached datasets to render
      let cart = safeParse(localStorage.getItem("cart")) || [];
      const prices = safeParse(localStorage.getItem("prices")) || [];
      const products = safeParse(localStorage.getItem("products")) || [];

      // cart:  [{ price: "price_1SFPw8HSsZyItNg3RbXsnb9u", quantity: 3 }, ...]
      // prices: [{ stripe_price_id: "price_1SFPwuHSsZyItNg3LjRjB0bV", stripe_product_id: "prod_1SFPwuHSsZyItNg3LjRjB0bV", price_cents: 2500, description: "" }, ...]
      // products: [{ stripe_product_id: "prod_1SFPwuHSsZyItNg3LjRjB0bV", name: "Product Name", discount_title: "", image: "https://..." }, ...]

      // Price lookup by price_id.
      // { stripe_price_id: { stripe_price_id, stripe_product_id, price_cents }, ... }
      const pricesById = new Map(prices.map((p) => [p?.stripe_price_id, p]));
      this.pricesById = pricesById;
      // Product lookup by product_id
      // { stripe_product_id: { stripe_product_id, name, discount_title, image }, ... }
      const productsById = new Map(products.map((p) => [p?.stripe_product_id, p]));
      this.productsById = productsById;

      // Drop cart items that reference prices that are no longer in pricesById.
      const filteredCart = (Array.isArray(cart) ? cart : []).filter((line) => pricesById.has(line?.price));
      if (filteredCart.length !== (Array.isArray(cart) ? cart.length : 0)) {
        // Keep localStorage in sync once stale items are removed.
        persistToLocalStorage("cart", filteredCart);
      }
      cart = filteredCart;

      this._renderCartItems(cart, pricesById, productsById);
      this._updateSubtotal(cart, pricesById);
      // Hide loader immediately after render
      this._setLoading(false);

      // Sync localStorage cart + etag after render
      // Prefer server-provided etag; if missing, compute a simple deterministic one
      const nextEtag = currentEtag || computeCartETag(cart);
      if (nextEtag) {
        persistString("cart_etag", nextEtag);
        this.element.dataset.currentCartEtag = nextEtag;
      }
    } catch (e) {
      // Fail quietly to avoid blocking UI
      // console.error("Cart offcanvas render failed", e);
    } finally {
      this._setLoading(false);
    }
  }

  // Reset the offcanvas markup after the Stripe checkout flow is dismissed.
  onOffcanvasHidden() {
    // this._restoreDefaultContent();
    if (this.hasStripeCartOffcanvasTarget) {
      this.stripeCartOffcanvasTarget.remove();
    }
    this.spykeCartOffcanvasTarget.classList.remove("d-none");
  }

  stripeCartOffcanvasTargetConnected(element) {
    console.log("Stripe cart offcanvas target connected", element);
    this.spykeCartOffcanvasTarget.classList.add("d-none");
    // We could use this to reset the offcanvas content when the checkout flow is dismissed.
  }

  // Triggered by "Back to Cart" link in checkout view (app/views/carts/checkout.turbo_stream.erb)
  showSpykeCart(element) {
    element.preventDefault();
    this.stripeCartOffcanvasTarget.remove();
    this.spykeCartOffcanvasTarget.classList.remove("d-none");
  }

  // ------- rendering helpers -------
  _renderCartItems(cart, pricesById, productsById) {
    if (!this.hasListTarget || !this.hasCartItemTemplateTarget) return;

    // Clear existing items (keep <template>)
    this.listTarget.querySelectorAll("li.cart-item").forEach((el) => el.remove());

    const tpl = this.cartItemTemplateTarget;
    const frag = document.createDocumentFragment();

    for (const line of Array.isArray(cart) ? cart : []) {
      const priceId = line?.price;
      const qty = Number(line?.quantity || 0);
      if (!priceId || !Number.isFinite(qty) || qty <= 0) continue;

      const price = pricesById.get(priceId);
      const product = price ? productsById.get(price?.stripe_product_id) : null;
      if (!price || !product) continue;

      const node = tpl.content.cloneNode(true);
      const li = node.querySelector("li.cart-item");
      if (li) li.dataset.priceId = priceId;

      // Populate fields
      const img = node.querySelector(".cart-item-image");
      if (img) {
        if (product.image) img.src = product.image;
        if (product.name) img.alt = product.name;
      }

      const titleEl = node.querySelector(".title");
      if (titleEl) titleEl.textContent = product.name || "Untitled";

      // Price description (between title and discount)
      const descEl = node.querySelector(".title-description");
      if (descEl) {
        const desc = price?.description;
        if (desc) {
          descEl.textContent = desc;
          descEl.style.display = "";
        } else {
          descEl.style.display = "none";
        }
      }

      // Discount: keep icon visible and only fill the text span
      const discountEl = node.querySelector(".title-discount");
      if (discountEl) {
        const d = product.discount_title;
        const discountTextEl = discountEl.querySelector(".discount-text");
        if (d) {
          if (discountTextEl) discountTextEl.textContent = d;
          discountEl.style.display = "";
        } else {
          discountEl.style.display = "none";
        }
      }

      const priceEl = node.querySelector(".cart-item-price");
      if (priceEl) {
        const cents = Number(price?.price_cents || 0) * qty;
        priceEl.textContent = this._formatCurrency(cents);
      }

      // Quantity input: reflect current qty and tag with price id
      const qtyInput = node.querySelector(".qty-input");
      if (qtyInput) {
        qtyInput.value = String(qty);
        qtyInput.dataset.priceId = priceId;
        // Stimulus wires this input to quantity_controller; storing priceId lets onQtyChange map back.
      }

      frag.appendChild(node);
    }

    this.listTarget.appendChild(frag);
  }

  _formatCurrency(cents) {
    // Keep method name for template calls; delegate to shared util
    return formatCurrency(cents);
  }

  _setLoading(isLoading) {
    try {
      if (this.hasSpinnerTarget) this.spinnerTarget.classList.toggle("d-none", !isLoading);
      if (this.hasListTarget) this.listTarget.classList.toggle("d-none", !!isLoading);
    } catch (_) {
      // no-op
    }
  }

  // Spinner hides right after render
  removeItem(event) {
    const li = event.currentTarget.closest("li.cart-item");
    if (!li) return;
    const priceId = li.dataset.priceId;
    if (!priceId) return;

    try {
      // Remove from local cart
      const cart = (safeParse(localStorage.getItem("cart")) || []).filter((i) => i?.price !== priceId);
      localStorage.setItem("cart", JSON.stringify(cart));

      // Remove line from DOM
      li.remove();

      // Update etag
      const etag = computeCartETag(cart);
      if (etag) persistString("cart_etag", etag);
      this.element.dataset.currentCartEtag = etag || "";
      // Update subtotal
      this._updateSubtotal(cart);
    } catch (_) {
      // ignore
    }
  }
  // Fired by the quantity controller's `quantity:change` event; keeps UI + storage in sync.
  onQtyChange(event) {
    const input = event.detail?.input || event.target;
    const li = input?.closest?.("li.cart-item");
    if (!li) return;
    const priceId = li.dataset.priceId || input?.dataset?.priceId;
    if (!priceId) return;
    let qty = Number(event.detail?.value ?? input?.value ?? 1);
    if (!Number.isFinite(qty)) qty = 1;
    qty = Math.min(Math.max(1, qty), 100);
    if (input) input.value = String(qty);
    this._updateCartLine(priceId, qty, li);
  }

  _updateCartLine(priceId, qty, liEl) {
    try {
      // Update local cart
      const cart = safeParse(localStorage.getItem("cart")) || [];
      const idx = cart.findIndex((i) => i?.price === priceId);
      if (idx >= 0) {
        cart[idx].quantity = Number(qty) || 1;
      } else {
        cart.push({ price: priceId, quantity: Number(qty) || 1 });
      }
      localStorage.setItem("cart", JSON.stringify(cart));

      // Update displayed line price
      const price = this.pricesById?.get(priceId);
      const cents = Number(price?.price_cents || 0) * (Number(qty) || 1);
      const priceEl = liEl?.querySelector(".cart-item-price");
      if (priceEl) priceEl.textContent = this._formatCurrency(cents);

      // Refresh etag
      const etag = computeCartETag(cart);
      if (etag) persistString("cart_etag", etag);
      // Update subtotal
      this._updateSubtotal(cart);
    } catch (_) {
      // ignore
    }
  }

  _updateSubtotal(cart, pricesById) {
    try {
      const lines = Array.isArray(cart) ? cart : safeParse(localStorage.getItem("cart")) || [];
      const pMap = pricesById || this.pricesById || new Map((safeParse(localStorage.getItem("prices")) || []).map((p) => [p?.stripe_price_id, p]));
      let cents = 0;
      for (const line of lines) {
        const pid = String(line?.price || "");
        const qty = Number(line?.quantity || 0);
        if (!pid || !Number.isFinite(qty) || qty <= 0) continue;
        const price = pMap.get(pid);
        if (!price) continue;
        cents += Number(price?.price_cents || 0) * qty;
      }
      if (this.hasSubtotalTarget) this.subtotalTarget.textContent = this._formatCurrency(cents);
      if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = !(cents > 0);
    } catch (_) {
      // ignore
    }
  }
}
